import React from 'react';
import modNodeChangelog from './modnode-changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { ModNodeState, NodeConfig, NodeField, NodeOutput, NodeType } from '../../';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Alert, AlertTitle, Stack } from '@mui/material';
import { useModules } from '@common/hooks/useModules';
import { FlowModule } from '@store/async';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { NodeAnchors, NodeBodyContainer, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Module Node',
  identifier: 'ModNode',
  command_id: 'node.module',
  background: '#eba73a',
  icon: 'module',
  moduleId: null,
  changelog: modNodeChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'node.module.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'node.module.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Complete',
      id: 'node.module.complete',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'node.module.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: ModNodeState = {
  moduleId: '',
};

export const fields: NodeField[] = [
  {
    id: 'moduleId',
    type: '',
    label: 'Module Id',
    description: 'The id of the module.',
  },
];

export const output: NodeOutput = {
  default: {
    node: {
      module: 'unknown',
    },
  },
  dynamic: null,
};

export const ModNode: React.FC<NodeType> = ({ id, selected, data }) => {
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { modules, error } = useModules();
  const { background, anchors, icon } = config;
  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'node.module') return;

  const state = command.state as ModNodeState;
  const moduleId = command.config?.moduleId;
  const mod = modules.find((mod: FlowModule) => mod.flow_id === moduleId || mod.flow_id === state.moduleId);

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={mod?.name || 'Module Node'} icon={icon} background={background} status={nodeStatus} />
      {error && (
        <NodeBodyContainer title={error}>
          <Alert sx={{ width: '100%' }} severity='error'>
            <AlertTitle>Failed to Load Modules</AlertTitle>
            {`A fatal error has occured. Please refresh the page and try again. If this issue persists, please contact support.\n\nError message: `}
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        </NodeBodyContainer>
      )}
      {mod?.description && <NodeBodyContainer title={mod?.description}>{mod?.description}</NodeBodyContainer>}
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
